var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import React from 'react';
import { autobind } from '@typed-decorators/autobind';
function intersectRect(a, b) {
    const top = Math.max(a.top, b.top);
    const bottom = Math.min(a.bottom, b.bottom);
    const left = Math.max(a.left, b.left);
    const right = Math.min(a.right, b.right);
    const width = right - left;
    const height = bottom - top;
    return { top, bottom, left, right, width, height };
}
function getRootRect() {
    const html = document.documentElement;
    const { body } = document;
    const width = html.clientWidth || body.clientWidth;
    const height = html.clientHeight || body.clientHeight;
    return { top: 0, left: 0, right: width, bottom: height, width, height };
}
// Try to locate the position using getValue, count, targetValue.
// This should perform within O(log n).
function binarySearch(count, getValue, targetValue) {
    let min = 0;
    let max = count;
    while (min < max) {
        const mid = (min + max) >> 1;
        const value = getValue(mid);
        if (value < targetValue) {
            min = mid + 1;
        }
        else if (value > targetValue) {
            max = mid - 1;
        }
        else {
            return mid;
        }
    }
    return min;
}
function getWindowWidth() {
    return window.innerWidth ||
        document.documentElement.clientWidth ||
        document.getElementsByTagName('body')[0].clientWidth;
}
function getWindowHeight() {
    return window.innerHeight ||
        document.documentElement.clientHeight ||
        document.getElementsByTagName('body')[0].clientHeight;
}
// Size list becomes unbelivably large if the user goes down too much -
// 1000 items is about 5KB, if 'null' is considered.
// Obviously this is not acceptable to store into sessionStorage, therefore
// we need very simple compression to compact this down to few hundred bytes.
//
// - s (n) - set stripe (sizeStep), if set, each 'w' will write N more '0'
// - w (n) - write N
// - n (n) - write null N times
// For example, 's3w100w100w200n100' becomes...
// [100, 0, 0, 0, 100, 0, 0, 0, 200, 0, 0, 0, ... 100 null items]
function encodeRLE(input) {
    let sizeStep = 1;
    const result = [];
    // First, determine size step...
    {
        let i = 0;
        while (input[i] == null || input[i] === 0) {
            i += 1;
            if (i > input.length) {
                break;
            }
        }
        while (input[i + sizeStep] === 0) {
            sizeStep += 1;
        }
    }
    result.push(`s${sizeStep}`);
    // Then process the data.
    let nullSize = 0;
    for (let i = 0; i < input.length; i += 1) {
        if (input[i] == null) {
            nullSize += 1;
        }
        else {
            if (nullSize > 0) {
                result.push(`n${nullSize}`);
                nullSize = 0;
            }
            result.push(`w${input[i]}`);
            i += sizeStep - 1;
        }
    }
    if (nullSize > 0) {
        result.push(`n${nullSize}`);
        nullSize = 0;
    }
    return result.join('');
}
function decodeRLE(input) {
    let sizeStep = 1;
    const output = [];
    for (let i = 0; i < input.length; i += 1) {
        const char = input[i];
        // Try to digest parameter number
        let numBuffer = '';
        while (input[i + 1] >= '0' && input[i + 1] <= '9') {
            numBuffer += input[i + 1];
            i += 1;
        }
        const num = parseInt(numBuffer, 10) || 0;
        switch (char) {
            case 's':
                sizeStep = num;
                break;
            case 'n':
                for (let j = 0; j < num; j += 1) {
                    output.push(null);
                }
                break;
            case 'w':
                output.push(num);
                for (let j = 1; j < sizeStep; j += 1) {
                    output.push(0);
                }
                break;
            default:
        }
    }
    return output;
}
export class VirtualizedList extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            paddingTop: 0,
            paddingBottom: 0,
            startIndex: 0,
            endIndex: 24,
            tempDelta: 0,
            forceRecalculate: false,
            fastScrolling: false,
        };
        this.anchorIndex = 0;
        this.prevWindowWidth = 0;
        this.sizeList = [];
        this.sizeCount = 0;
        this.sizeSum = 0;
        this.sizeAccList = [];
        this.sizeInvalidated = null;
        this.sizeStep = null;
        this.sizeVersion = 0;
        this.prevScrollAt = 0;
        this.prevScrollPos = 0;
        this.tempDelta = 0;
        this.overridenCount = null;
        this.scrollRestore = null;
        this.scrollRestoreJump = false;
        this.scrollPending = false;
        this.resizePending = false;
        this.containerNode = null;
        this.itemLoadeds = [];
        this.itemNodes = [];
        this.itemRefs = [];
        this.resizeId = null;
        this.scrollId = null;
        this.scrollEndId = null;
        this.scrollStabilizeId = null;
        this.stabilizeId = null;
        this.beforeScrollRestoration = null;
        this.loadSaveData();
    }
    resetVirtualizedList() {
        const { tabId } = this.props;
        sessionStorage.removeItem(`vlist-data-${tabId}`);
        this.setState({
            paddingTop: 0,
            paddingBottom: 0,
            startIndex: 0,
            endIndex: 24,
            tempDelta: 0,
            forceRecalculate: false,
            fastScrolling: false,
        });
        this.scrollRestoreJump = false;
        window.scrollTo(0, 0);
    }
    loadSaveData() {
        const { tabId, disableRestore } = this.props;
        let value = null;
        try {
            value = JSON.parse(sessionStorage[`vlist-data-${tabId}`] || 'null');
        }
        catch (e) {
            // Do nothing if failed
        }
        this.overridenCount = null;
        if (value != null) {
            // Restore save data...
            if (getWindowWidth() === value.windowWidth) {
                this.sizeList = decodeRLE(value.sizeList);
                this.sizeInvalidated = 0;
                const { sum, count } = this.sizeList.reduce((p, v) => (v != null
                    ? { count: p.count + 1, sum: p.sum + v }
                    : p), { count: 0, sum: 0 });
                this.sizeSum = sum;
                this.sizeCount = count;
            }
            this.overridenCount = value.count;
            if (!disableRestore) {
                this.scrollRestore = {
                    pos: value.anchorPos,
                    offset: value.anchorOffset,
                };
            }
        }
    }
    saveSaveData(tabId) {
        if (tabId == null || this.containerNode == null) {
            return;
        }
        const { count } = this.props;
        const containerTop = this.containerNode.getBoundingClientRect().top;
        const anchorOffset = this.getAccPos(this.anchorIndex - 1) + containerTop;
        const value = {
            sizeList: encodeRLE(this.sizeList),
            windowWidth: getWindowWidth(),
            count,
            anchorPos: this.anchorIndex,
            anchorOffset,
        };
        try {
            sessionStorage[`vlist-data-${tabId}`] = JSON.stringify(value);
        }
        catch (e) {
            // Do nothing if failed
        }
    }
    updateSize(index, size) {
        const previousSize = this.getSize(index);
        if (previousSize === size) {
            return;
        }
        this.sizeVersion += 1;
        if (previousSize == null) {
            this.sizeCount += 1;
            this.sizeSum += size;
        }
        else {
            this.sizeSum += size - previousSize;
        }
        this.sizeList[index] = size;
        if (this.sizeInvalidated == null) {
            this.sizeInvalidated = index;
        }
        else {
            this.sizeInvalidated = Math.min(this.sizeInvalidated, index);
        }
    }
    getSizeLength() {
        return this.sizeList.length;
    }
    getSize(index) {
        const current = this.sizeList[index];
        return current;
    }
    getAverageSize() {
        return (this.sizeSum / this.sizeCount) || 100;
    }
    getSizeStep() {
        if (this.sizeStep == null) {
            this.sizeStep = 1;
            let i = 0;
            while (this.getSize(i) === 0 || this.getSize(i) == null) {
                i += 1;
                if (i > this.getSizeLength()) {
                    this.sizeStep = null;
                    return 1;
                }
            }
            while (this.getSize(this.sizeStep + i) === 0) {
                this.sizeStep += 1;
            }
            if (this.sizeStep >= this.getSizeLength()) {
                this.sizeStep = null;
                return 1;
            }
        }
        return this.sizeStep;
    }
    getAccPos(index) {
        const { length } = this.sizeAccList;
        if (index < 0) {
            return 0;
        }
        // Try to get direct value, otherwise get inferred value.
        if (length > index) {
            return this.sizeAccList[index];
        }
        const sizeStep = this.getSizeStep();
        const accSize = this.getAverageSize() * (Math.ceil(index / sizeStep) * sizeStep -
            Math.floor(length / sizeStep) * sizeStep);
        if (length === 0) {
            return accSize;
        }
        return this.sizeAccList[length - 1] + accSize;
    }
    recalculateAccSize() {
        if (this.sizeInvalidated == null) {
            return;
        }
        const startIndex = Math.min(this.sizeInvalidated || 0, this.sizeAccList.length);
        let prev = 0;
        if (startIndex > 0) {
            prev = this.sizeAccList[startIndex - 1];
        }
        const sizeStep = this.getSizeStep();
        for (let i = startIndex; i < this.getSizeLength(); i += 1) {
            let entrySize = this.getSize(i);
            if (entrySize == null) {
                entrySize = i % sizeStep === 0
                    ? this.getAverageSize() * sizeStep
                    : 0;
            }
            const current = prev + entrySize;
            this.sizeAccList[i] = current;
            prev = current;
        }
        // Remaining values can be inferred from
        // sizeAccList[size] + avg * (i - size), therefore it's a waste of
        // computation to pre-compute this values.
        this.sizeInvalidated = null;
    }
    recalculateSizeImpl() {
        if (this.containerNode == null) {
            return;
        }
        // Recalculate all visible nodes for now
        // TODO Calculate new nodes only
        const { count } = this.props;
        const { paddingTop, paddingBottom, startIndex, endIndex } = this.state;
        const maxIndex = Math.min(endIndex, count);
        let shouldAcceptNonLoaded = false;
        // If there's nothing in the size buffer, and there is not a single
        // loaded item, we must use currently loaded items to correctly approximate
        // current position.
        if (this.sizeList.length === 0) {
            let hasLoaded = false;
            for (let i = startIndex; i <= maxIndex; i += 1) {
                if (this.itemNodes[i] != null && this.itemLoadeds[i]) {
                    hasLoaded = true;
                    break;
                }
            }
            shouldAcceptNonLoaded = !hasLoaded;
        }
        // Size is calculated by 'how much it influences the total height' -
        // Therefore in a grid, only the leftmost one receives the height.
        // This is acheived by comparing using the node's top position.
        // +                  - Initial position -> padding top
        // +---+ +---+ +---+  - v 0th, 1st, 2nd
        // |   | |   | |   |  - |
        // +---+ +---+ +---+  - |
        // +---+              - v 3rd
        // |   |              - |
        // +---+              - |
        // +                  - v end
        // However, because it shifts the position (even though 0th influences
        // the height, 3rd gets the value), the position needs to be adjusted
        // as well. This can be acheived by memorizing first non-zero index.
        const containerRect = this.containerNode.getBoundingClientRect();
        let pos = containerRect.top + paddingTop;
        let currentNonZeroIndex = startIndex;
        for (let i = startIndex; i <= maxIndex; i += 1) {
            const node = this.itemNodes[i];
            if (node == null) {
                return;
            }
            const nodePos = node.getBoundingClientRect().top;
            const diff = Math.round(nodePos - pos);
            if (diff !== 0) {
                // Adjust the position if all non-zero indices are loaded.
                let isLoaded = true;
                for (let j = currentNonZeroIndex; j < i; j += 1) {
                    if (!this.itemLoadeds[j]) {
                        isLoaded = false;
                        break;
                    }
                }
                if (isLoaded || shouldAcceptNonLoaded) {
                    this.updateSize(currentNonZeroIndex, diff);
                    for (let j = currentNonZeroIndex + 1; j < i; j += 1) {
                        this.updateSize(j, 0);
                    }
                }
                currentNonZeroIndex = i;
            }
            pos = nodePos;
        }
        // Compare the bottom position of the container, then set the last index.
        const endPos = containerRect.bottom - paddingBottom;
        {
            let isEndLoaded = true;
            for (let j = currentNonZeroIndex; j <= maxIndex; j += 1) {
                if (!this.itemLoadeds[j]) {
                    isEndLoaded = false;
                    break;
                }
            }
            if (isEndLoaded) {
                this.updateSize(currentNonZeroIndex, Math.round(endPos - pos));
                for (let j = currentNonZeroIndex + 1; j <= maxIndex; j += 1) {
                    this.updateSize(j, 0);
                }
            }
        }
    }
    recalculateSize() {
        // Pick the anchor index and memorize its position
        const anchorPos = this.getAccPos(this.anchorIndex - 1);
        // Actually run the size calculation logic
        this.recalculateSizeImpl();
        // Try to locate the anchor node and move to it. This accounts for
        // resizing due to the layout changes - Howevver, we still need to
        // calculate the size of nodes that aren't rendered. To account
        // for this, we compare approximate accumulated sizes and scroll until
        // there.
        this.recalculateAccSize();
        if (anchorPos != null) {
            const nextAnchorPos = this.getAccPos(this.anchorIndex - 1);
            const deltaDiff = anchorPos - nextAnchorPos;
            this.tempDelta += deltaDiff;
        }
    }
    invalidateSize() {
        // Pick the anchor index and memorize its position
        const anchorPos = this.getAccPos(this.anchorIndex - 1);
        // Reset store...
        this.sizeList = [];
        this.sizeCount = 0;
        this.sizeSum = 0;
        this.sizeAccList = [];
        this.sizeInvalidated = null;
        this.sizeStep = null;
        // Actually run the size calculation logic
        this.recalculateSizeImpl();
        // Try to locate the anchor node and move to it. This accounts for
        // resizing due to the layout changes - Howevver, we still need to
        // calculate the size of nodes that aren't rendered. To account
        // for this, we compare approximate accumulated sizes and scroll until
        // there.
        this.recalculateAccSize();
        if (anchorPos != null) {
            const nextAnchorPos = this.getAccPos(this.anchorIndex - 1);
            const deltaDiff = anchorPos - nextAnchorPos;
            this.tempDelta += deltaDiff;
        }
    }
    getViewportIntersection() {
        // Calculate which portion of nodes are visible, and set the padding /
        // visible nodes according to it.
        // Since VirtualizedList lacks declared 'parent' container, the only way to
        // calculate visible offset is to try intersection with all parent nodes...
        if (this.containerNode == null) {
            throw new Error('containerNode is null');
        }
        let currentNode = this.containerNode;
        let currentRect = this.containerNode.getBoundingClientRect();
        while (currentNode != null) {
            const parentNode = currentNode.parentElement;
            if (parentNode === document.body ||
                parentNode === document.documentElement ||
                parentNode == null) {
                break;
            }
            const parentStyle = window.getComputedStyle(parentNode);
            if (parentStyle.overflow === 'auto' || parentStyle.overflow === 'scroll') {
                currentRect = intersectRect(currentRect, parentNode.getBoundingClientRect());
            }
            currentNode = parentNode;
        }
        currentRect = intersectRect(currentRect, getRootRect());
        // currentRect contains viewable area of the node - use this to calculate
        // total visible area of the node.
        return currentRect;
    }
    scrollToAnchor(pos, offset) {
        if (this.containerNode == null)
            return 0;
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        const containerTop = this.containerNode.getBoundingClientRect().top;
        const anchorPos = this.getAccPos(pos - 1);
        const targetY = scrollY + containerTop +
            anchorPos - offset;
        window.scrollTo(0, targetY);
        return targetY;
    }
    recalculateViewport() {
        if (this.containerNode == null)
            return;
        this.recalculateAccSize();
        const containerTop = this.containerNode.getBoundingClientRect().top;
        let currentRect = this.getViewportIntersection();
        let scrollVelocity = 0;
        // If scrollRestore is specified, don't do any viewport bounds check.
        // Instead, render directly to the desired position, then set the scroll
        // position to there once it's rendered. (This shall be handled inside
        // componentDidUpdate, I think?)
        if (this.scrollRestore != null) {
            // We (should) have scroll restoration information in current stage,
            // as it's determined from accumulated size information, which is
            // (hopefully) provided from the saved data, or, augmented from first
            // few nodes. If it's augmented, it's still okay, since scroll correction
            // logic can continously adapt scroll position to the expected position.
            const anchorPos = this.getAccPos(this.scrollRestore.pos);
            const top = anchorPos + containerTop - this.scrollRestore.offset;
            const windowHeight = getWindowHeight();
            currentRect = {
                ...currentRect,
                top,
                bottom: top + windowHeight,
                height: windowHeight,
            };
            this.scrollPending = true;
            this.prevScrollAt = 0;
            // this.scrollToAnchor(this.scrollRestore.pos, this.scrollRestore.offset);
            // this.scrollRestore = null;
            this.scrollRestoreJump = true;
        }
        else {
            // Determine the current scrolling velocity using previous data.
            const scrollDiff = Date.now() - this.prevScrollAt;
            if (scrollDiff < 500) {
                scrollVelocity = this.prevScrollPos - containerTop;
            }
            this.prevScrollPos = containerTop;
            this.prevScrollAt = Date.now();
            // User is scrolling too fast; trigger fast scrolling mode.
            // TODO Unset this once the user slows down
            if (Math.abs(scrollVelocity / (scrollDiff / 1000)) > 4000) {
                const { fastScrolling } = this.state;
                if (!fastScrolling) {
                    this.setState({ fastScrolling: true });
                }
            }
        }
        // Translate viewable rect to scroll position including velocity and
        // overscanning.
        const { count, overscanHeight = 500 } = this.props;
        const maxCount = this.overridenCount != null
            ? Math.max(this.overridenCount, count)
            : count;
        const { tempDelta: committedTempDelta, startIndex: prevStartIndex, endIndex: prevEndIndex, } = this.state;
        const visibleTop = currentRect.top - containerTop - overscanHeight +
            committedTempDelta - this.tempDelta +
            (scrollVelocity < 0
                ? Math.max(-800, scrollVelocity * 1.5)
                : Math.min(800, scrollVelocity * 0.8));
        const visibleBottom = currentRect.bottom - containerTop + overscanHeight +
            committedTempDelta - this.tempDelta +
            (scrollVelocity > 0
                ? Math.min(800, scrollVelocity * 1.5)
                : Math.max(-800, scrollVelocity * 0.8));
        let visibleTopPos = binarySearch(count, (v) => this.getAccPos(v), visibleTop);
        let visibleBottomPos = binarySearch(count, (v) => this.getAccPos(v), visibleBottom);
        const sizeStep = this.getSizeStep();
        // Try to snap the position to cloest non-zero value.
        // This is necessary to prevent non-aligned position access, therefore
        // allowing the node to wrap and breaking the whole layout.
        visibleTopPos -= sizeStep;
        visibleTopPos = Math.floor(visibleTopPos / sizeStep) * sizeStep;
        // Find the next non-zero value for bottom, and navigate to its end...
        visibleBottomPos += sizeStep * 2;
        visibleBottomPos = Math.ceil(visibleBottomPos / sizeStep) * sizeStep - 1;
        visibleTopPos = Math.max(0, visibleTopPos);
        visibleBottomPos = Math.min(count, visibleBottomPos);
        // Calculate anchor index separately...
        const anchorTop = currentRect.top - containerTop +
            committedTempDelta - this.tempDelta;
        this.anchorIndex =
            Math.ceil(binarySearch(count, (v) => this.getAccPos(v), anchorTop) /
                sizeStep) * sizeStep;
        const { onRowsRendered } = this.props;
        const paddingTop = Math.round(this.getAccPos(visibleTopPos - 1));
        const paddingBottom = Math.round(this.getAccPos(maxCount) -
            this.getAccPos(visibleBottomPos));
        if (onRowsRendered != null &&
            !this.resizePending &&
            (prevStartIndex !== visibleTopPos ||
                prevEndIndex !== visibleBottomPos)) {
            onRowsRendered(visibleTopPos, visibleBottomPos);
        }
        let shouldForceRecalculate = false;
        for (let i = visibleTopPos; i < prevStartIndex; i += 1) {
            if (this.getSize(i) == null) {
                shouldForceRecalculate = true;
                break;
            }
        }
        this.setState({
            startIndex: visibleTopPos,
            endIndex: visibleBottomPos,
            paddingTop,
            paddingBottom,
            tempDelta: this.tempDelta,
            forceRecalculate: shouldForceRecalculate,
        });
    }
    commitTempDelta() {
        if (this.tempDelta === 0) {
            return;
        }
        const delta = this.tempDelta;
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        this.tempDelta = 0;
        window.scrollTo(0, scrollY - delta);
    }
    attachListeners() {
        this.prevWindowWidth = document.documentElement.clientWidth;
        if ('scrollRestoration' in history) {
            this.beforeScrollRestoration = history.scrollRestoration;
            history.scrollRestoration = 'manual';
        }
        window.addEventListener('unload', this.handleUnload);
        window.addEventListener('resize', this.handleResize, true);
        document.addEventListener('scroll', this.handleScroll, true);
    }
    detachListeners() {
        if ('scrollRestoration' in history &&
            this.beforeScrollRestoration != null) {
            history.scrollRestoration = this.beforeScrollRestoration;
        }
        window.removeEventListener('unload', this.handleUnload);
        window.removeEventListener('resize', this.handleResize, true);
        document.removeEventListener('scroll', this.handleScroll, true);
    }
    componentDidMount() {
        this.recalculateSize();
        this.recalculateViewport();
        this.attachListeners();
    }
    getSnapshotBeforeUpdate(prevProps) {
        const { tabId } = this.props;
        if (prevProps.tabId !== tabId) {
            this.saveSaveData(prevProps.tabId);
        }
        return null;
    }
    componentDidUpdate(prevProps) {
        const { tabId } = this.props;
        const { forceRecalculate } = this.state;
        if (tabId != null && prevProps.tabId !== tabId) {
            this.invalidateSize();
            this.loadSaveData();
            this.recalculateViewport();
            this.tempDelta = 0;
        }
        else {
            if (this.resizePending) {
                return;
            }
            if (this.scrollRestoreJump && this.scrollRestore != null) {
                this.scrollToAnchor(this.scrollRestore.pos, this.scrollRestore.offset);
                this.scrollRestoreJump = false;
                this.scrollRestore = null;
                this.overridenCount = null;
                return;
            }
            if (forceRecalculate) {
                this.recalculateSize();
                this.recalculateViewport();
            }
            else if (this.stabilizeId == null) {
                this.stabilizeId = requestAnimationFrame(() => {
                    this.stabilizeId = requestAnimationFrame(() => {
                        this.stabilizeId = null;
                        this.recalculateSize();
                        if (!this.scrollPending) {
                            this.recalculateViewport();
                        }
                    });
                });
            }
        }
    }
    componentWillUnmount() {
        const { tabId } = this.props;
        this.saveSaveData(tabId);
        this.detachListeners();
        if (this.resizeId != null) {
            cancelAnimationFrame(this.resizeId);
        }
        if (this.scrollId != null) {
            window.clearTimeout(this.scrollId);
        }
        if (this.scrollEndId != null) {
            window.clearTimeout(this.scrollEndId);
        }
        if (this.stabilizeId != null) {
            cancelAnimationFrame(this.stabilizeId);
        }
        if (this.scrollStabilizeId != null) {
            cancelAnimationFrame(this.scrollStabilizeId);
        }
    }
    handleScroll() {
        if (this.resizePending) {
            return;
        }
        this.scrollPending = true;
        if (this.scrollEndId != null) {
            window.clearTimeout(this.scrollEndId);
            this.scrollEndId = null;
        }
        if (this.scrollId != null) {
            return;
        }
        this.scrollId = window.setTimeout(() => {
            this.scrollId = null;
            if (this.scrollStabilizeId != null) {
                return;
            }
            this.scrollStabilizeId = requestAnimationFrame(() => {
                this.scrollStabilizeId = null;
                if (this.scrollEndId != null) {
                    window.clearTimeout(this.scrollEndId);
                }
                if (this.resizePending) {
                    return;
                }
                this.scrollEndId = window.setTimeout(this.handleScrollEnd, 200);
                this.recalculateViewport();
            });
        }, 50);
    }
    handleScrollEnd() {
        this.scrollEndId = null;
        this.scrollPending = false;
        this.setState({ fastScrolling: false, forceRecalculate: false });
        if (this.resizePending) {
            return;
        }
        this.commitTempDelta();
        this.recalculateViewport();
    }
    handleResize() {
        // We need to invalidate entire size data!
        if (this.resizeId == null) {
            this.resizePending = true;
            this.resizeId = requestAnimationFrame(() => {
                this.resizeId = null;
                const currentWidth = document.documentElement.clientWidth;
                if (currentWidth !== this.prevWindowWidth) {
                    this.invalidateSize();
                }
                this.recalculateViewport();
                this.prevWindowWidth = currentWidth;
                this.resizePending = false;
            });
        }
    }
    handleUnload() {
        const { tabId } = this.props;
        this.saveSaveData(tabId);
    }
    getStyle() {
        const { style } = this.props;
        const { paddingTop, paddingBottom, tempDelta } = this.state;
        return {
            ...style || {},
            paddingTop: `${paddingTop}px`,
            paddingBottom: `${paddingBottom}px`,
            transform: tempDelta != null
                ? `translateY(${tempDelta}px)`
                : undefined,
        };
    }
    renderItems() {
        const { count, innerClassName, innerStyle, renderItem, isLoaded } = this.props;
        const { startIndex, endIndex, fastScrolling } = this.state;
        const maxIndex = Math.min(endIndex, count - 1);
        const output = [];
        for (let i = startIndex; i <= maxIndex; i += 1) {
            // TODO Store ref functions and reuse them for the sake of optimization
            let ref = this.itemRefs[i];
            if (ref == null) {
                ref = this.itemRefs[i] = (node) => {
                    this.itemNodes[i] = node;
                };
            }
            const itemLoaded = isLoaded != null ? isLoaded(i) : true;
            this.itemLoadeds[i] = itemLoaded;
            let style = innerStyle;
            if (!itemLoaded && this.getSizeLength() > i) {
                const sizeStep = this.getSizeStep();
                const inferredHeight = this.getSize(Math.floor(i / sizeStep) * sizeStep);
                style = { ...(innerStyle || {}), height: `${inferredHeight}px` };
            }
            output.push(React.createElement("div", { className: innerClassName, ref: ref, style: style, key: i }, renderItem({ index: i, fastScrolling })));
        }
        return output;
    }
    render() {
        const { className } = this.props;
        return (React.createElement("div", { className: className, style: this.getStyle(), ref: (node) => {
                this.containerNode = node;
            } }, this.renderItems()));
    }
}
__decorate([
    autobind
], VirtualizedList.prototype, "resetVirtualizedList", null);
__decorate([
    autobind
], VirtualizedList.prototype, "handleScroll", null);
__decorate([
    autobind
], VirtualizedList.prototype, "handleScrollEnd", null);
__decorate([
    autobind
], VirtualizedList.prototype, "handleResize", null);
__decorate([
    autobind
], VirtualizedList.prototype, "handleUnload", null);
//# sourceMappingURL=VirtualizedList.js.map